import { useEffect, useMemo, useState } from "react";

import { useARC } from "providers/ARC/ARCProvider";
import { fetchWifiScan, fetchWifiStatus, getWifiApiBaseUrl } from "lib/wifiApi";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";

const POLL_MS = 12000;

export type RobotWifiState =
    | { kind: "loading" }
    | { kind: "unavailable" }
    | { kind: "disconnected" }
    | { kind: "connected"; signal: number | null };

function currentConnectionLabel(st: {
    error?: string | null;
    ssid?: string | null;
    connection?: string | null;
}): string | null {
    if (st.error != null && String(st.error).trim() !== "") {
        return null;
    }
    const raw = st.ssid?.trim() || st.connection?.trim() || "";
    if (!raw || raw === "--") {
        return null;
    }
    return raw;
}

export function useRobotWifiStatus(): RobotWifiState {
    const { selectedARC } = useARC();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const baseUrl = useMemo(
        () => getWifiApiBaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const [state, setState] = useState<RobotWifiState>({ kind: "loading" });

    useEffect(() => {
        if (!baseUrl) {
            setState({ kind: "unavailable" });
            return;
        }

        let cancelled = false;

        const tick = async () => {
            try {
                const st = await fetchWifiStatus(baseUrl);
                if (cancelled) return;

                const cur = currentConnectionLabel(st);
                if (!cur) {
                    setState({ kind: "disconnected" });
                    return;
                }

                let signal: number | null = null;
                try {
                    const scan = await fetchWifiScan(baseUrl);
                    if (cancelled) return;
                    const row = scan.networks.find((n) => n.ssid === cur);
                    signal = row?.signal ?? null;
                } catch {
                    /* scan optional for bars */
                }

                setState({ kind: "connected", signal });
            } catch {
                if (!cancelled) {
                    setState({ kind: "unavailable" });
                }
            }
        };

        setState({ kind: "loading" });
        void tick();
        const id = window.setInterval(() => void tick(), POLL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
    }, [baseUrl]);

    return state;
}
